import { memo, useState, useEffect, useCallback } from 'react';
import { X, Download, ExternalLink, Maximize2, Minimize2, Volume2, VolumeX } from 'lucide-react';
import { createPortal } from 'react-dom';
import { forceDownload } from '../../utils/mediaUtils';

/**
 * MediaPreviewModal - Global modal for previewing media content
 * Opens content in an overlay without navigating away from the app
 */
const MediaPreviewModal = memo(({ isOpen, onClose, url, type, title }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Reset loading state when URL/visibility changes (avoids stale state on reopen)
  useEffect(() => {
    if (isOpen && url) setIsLoading(true);
  }, [url, isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleDownload = useCallback(() => {
    if (!url) return;
    forceDownload(url, title || `download_${Date.now()}`);
  }, [url, title]);

  const handleOpenExternal = useCallback(() => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  if (!isOpen || !url) return null;

  const modalContent = (
    <div 
      className={`media-preview-overlay ${isFullscreen ? 'fullscreen' : ''}`}
      onClick={handleBackdropClick}
      data-testid="media-preview-modal"
    >
      <div className={`media-preview-container ${isFullscreen ? 'fullscreen' : ''}`}>
        {/* Header */}
        <div className="media-preview-header">
          <div className="media-preview-title">
            <span className="media-type-badge">{type?.toUpperCase() || 'MEDIA'}</span>
            <span className="media-title-text">{title || 'Vista Previa'}</span>
          </div>
          <div className="media-preview-actions">
            {(type === 'video' || type === 'audio') && (
              <button 
                className="media-action-btn"
                onClick={() => setIsMuted(!isMuted)}
                title={isMuted ? 'Activar sonido' : 'Silenciar'}
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            )}
            <button 
              className="media-action-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Salir pantalla completa' : 'Pantalla completa'}
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button 
              className="media-action-btn"
              onClick={handleOpenExternal}
              title="Abrir en nueva pestaña"
            >
              <ExternalLink size={18} />
            </button>
            <button 
              className="media-action-btn download"
              onClick={handleDownload}
              title="Descargar"
            >
              <Download size={18} />
            </button>
            <button 
              className="media-action-btn close"
              onClick={onClose}
              title="Cerrar (Esc)"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="media-preview-content">
          {isLoading && (
            <div className="media-preview-loading">
              <div className="loading-spinner" />
              <span>Cargando...</span>
            </div>
          )}

          {type === 'image' && (
            <img 
              src={url} 
              alt={title || 'Preview'} 
              className="media-preview-image"
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
          )}

          {type === 'video' && (
            <video 
              src={url} 
              controls 
              autoPlay
              muted={isMuted}
              className="media-preview-video"
              onLoadedData={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
          )}

          {type === 'audio' && (
            <div className="media-preview-audio-container">
              <div className="audio-visualizer">
                {[...Array(20)].map((_, i) => (
                  <div 
                    key={`audio-bar-${i}`} 
                    className="audio-bar"
                    style={{ animationDelay: `${i * 0.05}s` }}
                  />
                ))}
              </div>
              <audio 
                src={url} 
                controls 
                autoPlay
                muted={isMuted}
                className="media-preview-audio"
                onLoadedData={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
              />
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="media-preview-footer">
          <span className="media-url" title={url}>
            {url.length > 60 ? url.substring(0, 60) + '...' : url}
          </span>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document root
  return createPortal(modalContent, document.body);
});

MediaPreviewModal.displayName = 'MediaPreviewModal';

export default MediaPreviewModal;
